import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Divider, Drawer, message, Modal, Space} from 'antd';
import {useParams} from 'react-router-dom';

import NewsPreview from '@/page/component/NewsFormModal/NewsPreview';
import {AppStoreProvider} from '@/page/app/context';
import ExtraNewsSendAreaWrapper from './ExtraNewsSendAreaWrapper';
import {SendOutlined} from '@/icons';
import {convertTos, isEmptySelData} from '@/page/app/MsgSendPage/utils';
import {sendMessage} from './extra-news';

const ExtraNewsSendDrawer = forwardRef((props, ref) => {
    const ExtraNewsSendAreaWrapperRef = useRef();
    const [visible, setVisible] = useState(false);
    const [submiting, setSubmiting] = useState(false);
    const {newsId, multi} = props;
    const {appId} = useParams();

    function show() {
        setVisible(true);
    }

    function hide() {
        setVisible(false);
        ExtraNewsSendAreaWrapperRef.current.clear();
    }

    useImperativeHandle(ref, () => ({
        show,
        hide,
    }));

    function onSendClick() {
        const {toAll, tos, toUserCount} = ExtraNewsSendAreaWrapperRef.current.getData();
        if (!toAll && isEmptySelData(tos)) {
            message.warn('「发送范围」不能为空');
            return false;
        }

        Modal.confirm({
            title: '提示',
            content: '确定要发送消息？',
            okText: '确定发送',
            onOk: () => {
                setSubmiting(true);
                sendMessage({
                    toAll,
                    tos: toAll ? null : convertTos(tos),
                    toUserCount,
                    appId,
                    multi,
                    newsId,
                })
                    .then(() => {
                        message.success('发送成功');
                        hide();
                    })
                    .finally(() => {
                        setSubmiting(false);
                    });
            },
        });
    }

    return (
        <Drawer
            visible={visible}
            width={800}
            onClose={hide}
            destroyOnClose={false}
            title={'上传图文发送'}
        >
            <div style={{width: 280, margin: '0 auto'}}>
                <NewsPreview
                    selectable
                    editable={false}
                    newsById={props.newsById}
                    tmpIds={props.tmpIds}
                    currentTmpId={props.currentTmpId}
                />
            </div>
            <Divider />
            <Space size="large" direction="vertical" style={{display: 'flex'}}>
                <AppStoreProvider>
                    <ExtraNewsSendAreaWrapper ref={ExtraNewsSendAreaWrapperRef} appId={appId} />
                </AppStoreProvider>
                <Divider />
                <Button
                    type="primary"
                    loading={submiting}
                    icon={<SendOutlined />}
                    onClick={onSendClick}
                >
                    发送消息
                </Button>
            </Space>
        </Drawer>
    );
});

ExtraNewsSendDrawer.displayName = 'ExtraNewsSendDrawer';

ExtraNewsSendDrawer.propTypes = {
    /**
     * 为 true 表示发送多图文消息，否则是单图文
     */
    multi: PropTypes.bool.isRequired,

    /**
     * 当多图文时，是 ExtraUploadNews 中的 id，用于定位 item 列表
     * 单图文时，是 ExtraUploadNewsItem 中的 id
     */
    newsId: PropTypes.string.isRequired,

    /**
     * 图文所属的应用 id，但不一定是实际发送目标应用的 id
     */
    appId: PropTypes.string.isRequired,

    newsById: PropTypes.object.isRequired,
    tmpIds: PropTypes.array.isRequired,
    currentTmpId: PropTypes.string.isRequired,
};

export default ExtraNewsSendDrawer;
